import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { splitPenalty, formatPenalty } from '../utils/relegation';
import './RelegationModal.css';

// Выдача релегации гонщику: этап, штрафное время и причина.
// На один этап у гонщика одна релегация — выбор уже отштрафованного этапа
// подставляет ее значения, сохранение перезаписывает.
const RelegationModal = ({ eventId, rider, stages, relegations, onClose, onSaved }) => {
  const { t } = useLanguage();

  const riderRelegations = relegations || [];
  const findRelegation = (routeId) =>
    riderRelegations.find(item => String(item.route_id) === String(routeId)) || null;

  const initialStage = () => {
    const free = stages.find(stage => !findRelegation(stage.routeId));
    return String((free || stages[0] || {}).routeId || '');
  };

  const [routeId, setRouteId] = useState(initialStage);
  const [minutes, setMinutes] = useState(() => String(splitPenalty(findRelegation(initialStage())?.penalty_seconds).minutes));
  const [seconds, setSeconds] = useState(() => String(splitPenalty(findRelegation(initialStage())?.penalty_seconds).seconds));
  const [reason, setReason] = useState(() => findRelegation(initialStage())?.reason || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const existing = findRelegation(routeId);

  const handleStageChange = (value) => {
    setRouteId(value);

    const relegation = findRelegation(value);
    const penalty = splitPenalty(relegation?.penalty_seconds);
    setMinutes(String(penalty.minutes));
    setSeconds(String(penalty.seconds));
    setReason(relegation?.reason || '');
  };

  const penaltySeconds = (parseInt(minutes, 10) || 0) * 60 + (parseInt(seconds, 10) || 0);

  const handleSave = async () => {
    if (!routeId) return;

    if (penaltySeconds <= 0) {
      setError(t('results.relegation_error_empty'));
      return;
    }

    setSaving(true);
    setError('');

    try {
      await axios.post(`/api/events/${eventId}/relegations`, {
        relegation: {
          route_id: routeId,
          user_id: rider.user_id,
          penalty_seconds: penaltySeconds,
          reason: reason.trim()
        }
      });

      onSaved();
    } catch (err) {
      console.error('Failed to save relegation:', err);
      setError(err.response?.data?.errors?.join(', ') || err.response?.data?.error || t('results.relegation_error'));
      setSaving(false);
    }
  };

  const handleDelete = async (relegationId) => {
    if (!window.confirm(t('results.relegation_confirm_delete'))) return;

    setSaving(true);
    setError('');

    try {
      await axios.delete(`/api/events/${eventId}/relegations/${relegationId}`);
      onSaved();
    } catch (err) {
      console.error('Failed to delete relegation:', err);
      setError(err.response?.data?.error || t('results.relegation_error'));
      setSaving(false);
    }
  };

  const stageLabel = (stage) => {
    const relegation = findRelegation(stage.routeId);
    const suffix = relegation ? ` — ${formatPenalty(relegation.penalty_seconds)}` : '';

    return `${stage.number}. ${stage.name}${suffix}`;
  };

  return (
    <div className="relegation-modal-overlay" onClick={onClose}>
      <div className="relegation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="relegation-modal-header">
          <div>
            <h2>{t('results.relegation_modal_title')}</h2>
            <div className="relegation-modal-rider">{rider.name}</div>
          </div>
          <button type="button" className="relegation-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="relegation-modal-body">
          {stages.length === 0 ? (
            <div className="relegation-modal-empty">{t('results.relegation_no_stages')}</div>
          ) : (
            <>
              <label className="relegation-modal-field">
                <span>{t('results.relegation_stage')}</span>
                <select value={routeId} onChange={(e) => handleStageChange(e.target.value)} disabled={saving}>
                  {stages.map(stage => (
                    <option key={stage.routeId} value={stage.routeId}>{stageLabel(stage)}</option>
                  ))}
                </select>
              </label>

              <div className="relegation-modal-penalty">
                <label className="relegation-modal-field">
                  <span>{t('results.relegation_minutes')}</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    disabled={saving}
                  />
                </label>
                <label className="relegation-modal-field">
                  <span>{t('results.relegation_seconds')}</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    step="1"
                    value={seconds}
                    onChange={(e) => setSeconds(e.target.value)}
                    disabled={saving}
                  />
                </label>
              </div>

              <label className="relegation-modal-field">
                <span>{t('results.relegation_reason')}</span>
                <textarea
                  rows="3"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('results.relegation_reason_placeholder')}
                  disabled={saving}
                />
              </label>

              <p className="relegation-modal-hint">{t('results.relegation_hint')}</p>
            </>
          )}

          {riderRelegations.length > 0 && (
            <div className="relegation-modal-list">
              <div className="relegation-modal-list-title">{t('results.relegation_existing')}</div>
              {riderRelegations.map(relegation => {
                const stage = stages.find(item => String(item.routeId) === String(relegation.route_id));

                return (
                  <div key={relegation.id} className="relegation-modal-list-item">
                    <div>
                      <strong>{formatPenalty(relegation.penalty_seconds)}</strong>
                      {' — '}
                      {stage ? `${stage.number}. ${stage.name}` : `#${relegation.route_id}`}
                      {relegation.reason && <div className="relegation-modal-list-reason">{relegation.reason}</div>}
                    </div>
                    <button
                      type="button"
                      className="relegation-modal-delete"
                      onClick={() => handleDelete(relegation.id)}
                      disabled={saving}
                    >
                      {t('results.relegation_delete')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {error && <div className="relegation-modal-error">{error}</div>}
        </div>

        <div className="relegation-modal-footer">
          <button type="button" className="relegation-modal-cancel" onClick={onClose} disabled={saving}>
            {t('results.relegation_cancel')}
          </button>
          <button
            type="button"
            className="relegation-modal-save"
            onClick={handleSave}
            disabled={saving || stages.length === 0}
          >
            {existing ? t('results.relegation_update') : t('results.relegation_save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RelegationModal;
